import React, { useState } from "react";
import { Card, Table, Tag, Select, Space } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import urls from "../../../../urls";
import { usePurchaseOrderShipments } from "../../../../data/hooks";

const statusColors = {
  draft: "default",
  shipped: "blue",
  in_transit: "orange",
  received: "green",
  completed: "cyan",
  cancelled: "red",
};

const statusLabels = {
  draft: "Concept",
  shipped: "Verzonden",
  in_transit: "Onderweg",
  received: "Ontvangen",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

const PurchaseOrderShipmentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State declarations must come before useEffect hooks that use them
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  React.useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrderShipmentsFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      } else {
        // Default: hide cancelled and completed shipments
        setStatusFilter("active");
      }
    } catch (error) {
      console.error("Error initializing filters:", error);
      // Fallback to default values if localStorage fails
      setStatusFilter("active");
      setSupplierFilter(null);
      setCurrentPage(1);
    }
  }, []);

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    try {
      const filters = {
        supplierFilter,
        statusFilter,
        currentPage,
      };
      localStorage.setItem("purchaseOrderShipmentsFilters", JSON.stringify(filters));
    } catch (error) {
      console.warn("Failed to save filters to localStorage:", error);
      // Silently fail - filters will work but won't persist
    }
  }, [supplierFilter, statusFilter, currentPage]);

  // Use hook to fetch shipments
  const { shipments, loading } = usePurchaseOrderShipments();

  // Sync filters to URL
  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  // Get unique suppliers for filter
  const suppliers = [
    ...new Map(shipments.map((s) => [s.supplierId, { id: s.supplierId, name: s.supplierName }])).values(),
  ];

  // Apply filters
  const filteredShipments = shipments.filter((s) => {
    if (supplierFilter && s.supplierId !== supplierFilter) return false;
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "active") {
        // Hide cancelled and completed shipments
        if (s.status === "cancelled" || s.status === "completed") return false;
      } else {
        if (s.status !== statusFilter) return false;
      }
    }
    return true;
  });

  const columns = [
    {
      title: t("shipment.shipment") + " #",
      dataIndex: "shipmentNumber",
      key: "shipmentNumber",
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      render: (name) => name || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColors[status] || "default"}>{statusLabels[status] || status}</Tag>,
    },
    {
      title: t("order.purchase_orders"),
      key: "sourceOrders",
      render: (_, record) => (
        <Space wrap>
          {record.sourceOrders?.map((so) => (
            <Tag
              key={so.purchaseOrderId}
              color="blue"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${urls.purchaseOrdersList}/${so.purchaseOrderId}`);
              }}
            >
              {so.purchaseOrderNumber}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t("total_products"),
      dataIndex: "totalProducts",
      key: "totalProducts",
    },
    {
      title: t("total_price"),
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (v) => (v ? `$ ${v.toFixed(2)}` : "-"),
    },
    {
      title: t("tracking_code"),
      dataIndex: "trackingCode",
      key: "trackingCode",
      render: (v) => v || "-",
    },
    {
      title: t("date"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (sec) => (sec ? dayjs.unix(sec).format("DD-MM-YYYY HH:mm") : "-"),
    },
  ];

  return (
    <>
      <h2>{t("purchase_order_shipments")}</h2>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Space>
            <Select
              allowClear
              placeholder={t("supplier.supplier")}
              style={{ minWidth: 200 }}
              value={supplierFilter}
              onChange={(v) => {
                setSupplierFilter(v);
                setCurrentPage(1);
                updateSearchParams({ supplier: v, page: null });
              }}
              options={suppliers.map((s) => ({ value: s.id, label: s.name || s.id }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ minWidth: 150 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                updateSearchParams({ status: v, page: null });
              }}
              options={[
                { value: "active", label: "Actief (zonder geannuleerd/afgerond)" },
                ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
              ]}
            />
          </Space>
        </div>
        <Table
          loading={loading}
          dataSource={filteredShipments}
          columns={columns}
          rowKey="id"
          pagination={{
            current: currentPage,
            onChange: (page) => {
              setCurrentPage(page);
              updateSearchParams({ page: String(page) });
            },
          }}
          onRow={(record) => ({
            onClick: () => navigate(`${urls.purchaseOrderShipmentDetail}${record.id}`),
            style: { cursor: "pointer" },
          })}
        />
      </Card>
    </>
  );
};

export default PurchaseOrderShipmentsList;
